<template>
	<el-dialog
		:visible.sync="innerVisible"
		:title="$t('enterprise.message-subscription')"
		custom-class="c-custom-dialog c-custom-dialog-with-fields"
		:before-close="handleClose"
		:close-on-click-modal="false"
		destroy-on-close
	>
		<DialogMaskImage />

		<FieldsFormPanel
			ref="panelRef"
			:personal-fields="personalFields"
			:send-verify-code="handleSendVerifyCode"
			@update-area-code="handleAreaCodeUpdate"
			@update-verification="handleVerificationUpdate"
		>
			<template #tip>
				<p v-if="subscriptionDesc" class="tip-content">{{ subscriptionDesc }}</p>
			</template>
			<template #append>
				<el-form ref="eventFormRef" :model="eventFormData" :rules="eventRules" label-position="left">
					<el-form-item prop="eventParamList">
						<template #label>
							<ellipsis-two-line-tooltip :text="$t('enterprise.field.eventName')" />
						</template>
						<el-select v-model="eventFormData.eventParamList" multiple collapse-tags>
							<el-option
								v-for="item in eventNameList"
								:key="item.id"
								:label="item.eventName"
								:value="item.id"
							></el-option>
						</el-select>
					</el-form-item>
				</el-form>
			</template>
		</FieldsFormPanel>

		<div v-if="personalFields.length" slot="footer" class="dialog-footer">
			<el-button @click="handleClose">{{ $t('enterprise.cancel') }}</el-button>
			<el-button :loading="loading" type="primary" @click="submitForm">{{ $t('enterprise.submit') }}</el-button>
		</div>
	</el-dialog>
</template>

<script setup>
import { ref, defineExpose, getCurrentInstance, nextTick } from 'vue';
import { Message } from 'irm-ui';
import { useModuleData } from '@/rebuild/hooks/data/useModuleData';
import FieldsFormPanel from '@rebuild/components/custom-fields/FieldsFormPanel.vue';
import { usePersonalFields } from '@rebuild/components/custom-fields/usePersonalFields';
import { SYSTEM_FIELD_MAP } from '@rebuild/components/custom-fields/systemFieldMap';
import DialogMaskImage from '@rebuild/components/DialogMaskImage.vue';
import EllipsisTwoLineTooltip from '@rebuild/components/custom-fields/EllipsisTwoLineTooltip.vue';

const { proxy } = getCurrentInstance();
const getFieldValidTips = prop =>
	proxy.$t(`enterprise.normal-field.valid-tips`, { label: proxy.$t(`enterprise.normal-field.${prop}`) });

const { message_subscription_messageMailCode, message_subscription_create, message_event, message_contact_config } =
	useModuleData();

const {
	personalFields,
	initDynamicField,
	processCustomFields,
	saveCacheDynamicFieldConfig,
	getCacheDynamicFieldConfig
} = usePersonalFields();

const panelRef = ref(null);
const eventFormRef = ref(null);
const innerVisible = ref(false);
const loading = ref(false);
const areaCode = ref('+86');
const subscriptionDesc = ref('');
const eventNameList = ref([]);

const eventFormData = ref({
	eventParamList: []
});
const eventRules = ref({
	eventParamList: [
		{
			required: true,
			message: proxy.$t('enterprise.field.eventName-tips'),
			trigger: 'blur'
		}
	]
});

const messageMap = {
	10015: proxy.$t('enterprise.normal-field.msg-box.10015'),
	10016: proxy.$t('enterprise.normal-field.msg-box.10016'),
	10017: proxy.$t('enterprise.normal-field.msg-box.10017'),
	10018: proxy.$t('enterprise.normal-field.msg-box.10018'),
	10019: proxy.$t('enterprise.normal-field.msg-box.10019'),
	10020: proxy.$t('enterprise.normal-field.msg-box.10020'),
	10021: proxy.$t('enterprise.normal-field.msg-box.10021')
};

const queryMessageEvent = async () => {
	await message_event.refresh();
	eventNameList.value = message_event.data?.data || [];
};

const queryMessageContactConfig = async () => {
	await message_contact_config.refresh();
	const data = message_contact_config.data?.data || {};
	subscriptionDesc.value = data.subscriptionDesc || '';

	const { languageDynamicFieldConfigVO, dynamicFieldConfigVO } = data;
	initDynamicField(languageDynamicFieldConfigVO);
	saveCacheDynamicFieldConfig(dynamicFieldConfigVO);
};

const initSubscriptionConfig = async () => {
	await Promise.all([queryMessageEvent(), queryMessageContactConfig()]);
};

const open = async () => {
	innerVisible.value = true;
	await initSubscriptionConfig();
};

const handleAreaCodeUpdate = value => {
	areaCode.value = value || '+86';
};

const validateEventForm = () =>
	new Promise(resolve => {
		if (!eventFormRef.value) {
			resolve(true);
			return;
		}
		eventFormRef.value.validate(valid => {
			resolve(valid);
		});
	});

const getSystemValue = key => {
	const item = personalFields.value.find(field => SYSTEM_FIELD_MAP[String(field.systemType)] === key);
	return item?.dynamicFieldValue || '';
};

const buildParams = () => {
	const eventParamListFormatted = eventNameList.value
		.filter(item => eventFormData.value.eventParamList.includes(item.id))
		.map(item => ({
			groupId: item.groupId,
			eventName: item.eventName
		}));
	return {
		name: getSystemValue('name'),
		company: getSystemValue('company'),
		position: getSystemValue('position'),
		email: getSystemValue('email'),
		phone: getSystemValue('phone'),
		areaCode: areaCode.value,
		authCode: authCode.value,
		eventParamList: eventParamListFormatted
	};
};

const handleError = err => {
	const errorCode = err?.response?.data?.code;
	Message.error(messageMap[errorCode] || proxy.$t('enterprise.error'));
};

const authCode = ref('');
const handleSendVerifyCode = async ({ email }) => {
	if (!email) {
		Message.error(`${getFieldValidTips('email')}`);
		throw new Error('email required');
	}

	const params = buildParams();
	delete params.authCode;

	await message_subscription_messageMailCode.refresh(params);
	if (message_subscription_messageMailCode.error) {
		handleError(message_subscription_messageMailCode.error);
		throw message_subscription_messageMailCode.error;
	}
	Message.success(proxy.$t('enterprise.normal-field.code-sent-to-email'));
};
const handleVerificationUpdate = value => {
	authCode.value = value || '';
};

const submitForm = async () => {
	const panelValid = panelRef.value ? await panelRef.value.validate() : true;
	const eventValid = await validateEventForm();
	if (!panelValid || !eventValid) return;

	loading.value = true;
	try {
		const params = buildParams();
		const processedCustomFields = processCustomFields();
		if (processedCustomFields.length > 0) {
			params.newAddDataFiled = processedCustomFields;
		}
		const cacheConfig = getCacheDynamicFieldConfig();
		if (cacheConfig) {
			params.dynamicFieldConfigVO = cacheConfig;
		}

		await message_subscription_create.refresh(params);
		if (message_subscription_create.error) {
			handleError(message_subscription_create.error);
			return;
		}
		Message.success(proxy.$t('enterprise.normal-field.subscribe-success'));
		handleClose();
	} finally {
		loading.value = false;
	}
};

const handleClose = () => {
	innerVisible.value = false;
	areaCode.value = '+86';
	authCode.value = '';
	eventFormData.value.eventParamList = [];
	personalFields.value.forEach(item => {
		proxy.$set(item, 'dynamicFieldValue', '');
	});
	nextTick(() => {
		panelRef.value?.clearValidate?.();
		panelRef.value?.resetFields?.();
		eventFormRef.value?.clearValidate?.();
		eventFormRef.value?.resetFields?.();
	});
};

defineExpose({ open });
</script>

<style lang="less" scoped>
/deep/.c-custom-dialog {
	width: auto;
	min-width: 360px;
	max-width: 600px;
	background: linear-gradient(180deg, #e3eaff 0%, #fff 19.44%);

	.el-dialog__header {
		padding: 32px;
		border-bottom: none;

		.el-dialog__title {
			color: #1d2129;
			font-size: 20px;
			font-weight: 500;
			line-height: 28px;
		}
	}
	.el-dialog__headerbtn {
		top: 30px;
		right: 28px;
	}
	.el-dialog__body {
		padding-top: 16px;
		.el-form-item__content {
			width: 350px;
		}
	}
}
/deep/ .el-dialog .el-form .el-form-item__label {
	width: 100px !important;
}
/deep/ .el-dialog .el-form .el-form-item__content {
	margin-left: 110px !important;
}
/deep/ .el-form .el-form-item.is-required:not(.is-no-asterisk) > .el-form-item__label::after {
	line-height: 28px;
}
.tip-content {
	padding-bottom: 48px;
	color: #4e5969;
	font-size: 14px;
	font-weight: 400;
	line-height: 18px;
	width: 460px;
}
</style>
